import { calculatePeaksSync } from './waveformDrawerFunctions';

export const MIN_PEAKS_RESOLUTION = 1024;
export const PEAKS_RESOLUTION_FACTOR = 2; // peaks per CSS pixel
const DEFAULT_AMPLITUDE_SCALE = 0.85;

export class AudioWave {
	constructor() {
		this.ctx = null;
		this.buffer = null;
		this.duration = 0;
		this.peaks = null;
		this.peaksCache = new Map();
		this.peaksComputing = new Map();
		this.sourceNode = null;
	}

	initContext() {
		if (!this.ctx) {
			const AudioContext = window.AudioContext || window.webkitAudioContext;
			this.ctx = new AudioContext();
		}
	}

	calculatePeaks(resolution) {
		this.peaks = calculatePeaksSync(this.buffer, resolution);
	}

	async draw(canvas, devicePixelRatio, viewport) {
		if (!canvas || !this.buffer) return;

		const displayWidth = Math.floor(canvas.width / devicePixelRatio);
		const displayHeight = Math.floor(canvas.height / devicePixelRatio);

		const pixelsPerPeak = 0.125; // 0.5 => 2 peaks/pixel; 1 => 1 peak/pixel

		const desiredRes = Math.max(MIN_PEAKS_RESOLUTION, Math.round(displayWidth / pixelsPerPeak));

		const res = this.$clampResolution(desiredRes, this.buffer);

		if (this.peaksCache.has(res)) {
			const peaks = this.peaksCache.get(res);
			this.$drawFromPeaks(peaks, canvas, displayWidth, displayHeight, devicePixelRatio, viewport, this.duration);
		} else {
			// dibujar versión rápida (si existe una resolución menor en cache) para evitar huecos
			const fallback = this.$findBestFallback(displayWidth, this.peaksCache);
			if (fallback)
				this.$drawFromPeaks(
					fallback.peaks,
					canvas,
					displayWidth,
					displayHeight,
					devicePixelRatio,
					viewport,
					this.duration
				);
			const peaks = await this.$getPeaksForResolution(res, this.buffer, this.peaksCache, this.peaksComputing);
			this.$drawFromPeaks(peaks, canvas, displayWidth, displayHeight, devicePixelRatio, viewport, this.duration);
		}
	}

	async loadFileFrom(url) {
		const response = await fetch(url);
		if (!response.ok) throw new Error('Error loading audio file');

		const arrayBuffer = await response.arrayBuffer();
		this.buffer = await this.ctx.decodeAudioData(arrayBuffer);
		this.duration = this.buffer.duration;
	}

	play(fromSeconds = 0) {
		this.sourceNode = this.ctx.createBufferSource();
		this.sourceNode.buffer = this.buffer;
		this.sourceNode.connect(this.ctx.destination);
		this.sourceNode.start(0, fromSeconds);
	}

	stop() {
		if (this.sourceNode) {
			try {
				this.sourceNode.stop();
			} catch (e) {}

			try {
				this.sourceNode.disconnect();
			} catch (e) {}
			this.sourceNode = null;
		}
	}

	$clampResolution(resolution, audioBuffer) {
		const maxPeaksResolution = 65536;
		const max = Math.min(maxPeaksResolution, audioBuffer ? audioBuffer.length : maxPeaksResolution);

		return Math.max(MIN_PEAKS_RESOLUTION, Math.min(max, Math.round(resolution)));
	}

	$findBestFallback(displayWidth, peaksCache) {
		if (!peaksCache.size) return null;

		const target = Math.max(MIN_PEAKS_RESOLUTION, displayWidth * PEAKS_RESOLUTION_FACTOR);
		let best = null;
		let bestDiff = Infinity;
		for (const [res, peaks] of peaksCache.entries()) {
			const diff = Math.abs(res - target);
			if (diff < bestDiff) {
				bestDiff = diff;
				best = { res, peaks };
			}
		}
		return best;
	}

	$getPeaksForResolution(resolution, audioBuffer, peaksCache, peaksComputing) {
		const resolutionClamped = this.$clampResolution(resolution, audioBuffer);
		if (peaksCache.has(resolutionClamped)) {
			return Promise.resolve(peaksCache.get(resolutionClamped));
		}
		if (peaksComputing.has(resolutionClamped)) {
			return peaksComputing.get(resolutionClamped);
		}

		const p = new Promise(resolve => {
			const run = () => {
				const peaks = calculatePeaksSync(audioBuffer, resolutionClamped);
				peaksCache.set(resolutionClamped, peaks);
				peaksComputing.delete(resolutionClamped);
				resolve(peaks);
			};
			if (typeof window.requestIdleCallback === 'function') {
				requestIdleCallback(run, { timeout: 500 });
			} else {
				setTimeout(run, 50);
			}
		});
		peaksComputing.set(resolutionClamped, p);
		return p;
	}

	$drawFromPeaks(peaks, canvas, displayWidth, displayHeight, devicePixelRatio, viewport, audioDuration) {
		const ctx = canvas.getContext('2d');
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.save();
		ctx.setTransform(1, 0, 0, 1, 0, 0);
		ctx.scale(devicePixelRatio, devicePixelRatio);
		ctx.fillStyle = '#2196F3';

		const totalPeaks = peaks.length;
		const startIdx = Math.floor((viewport.startSec / audioDuration) * totalPeaks);
		const endIdx = Math.max(startIdx + 1, Math.floor((viewport.endSec / audioDuration) * totalPeaks));
		const visibleCount = endIdx - startIdx;
		if (visibleCount <= 0) {
			ctx.restore();
			return;
		}

		const ampScale = Math.max(0.5, Math.min(1, DEFAULT_AMPLITUDE_SCALE));
		const maxWaveHeight = displayHeight * ampScale;

		for (let x = 0; x < displayWidth; x++) {
			const rel = displayWidth > 1 ? x / (displayWidth - 1) : 0;
			const peakPos = Math.floor(startIdx + rel * (visibleCount - 1));
			const v = peaks[peakPos] || 0;
			const h = Math.max(1, v * maxWaveHeight);
			const y = (displayHeight - h) / 2;
			ctx.fillRect(x, y, 1, h);
		}
		ctx.restore();
	}
}
